<template>
  <div class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="bg-white shadow rounded-lg mb-6">
      <div class="px-6 py-4 border-b border-gray-200">
        <h1 class="text-2xl font-bold text-gray-900">Panel de Administración</h1>
        <p class="text-gray-600 mt-1">Gestiona usuarios, cuotas y configuraciones del sistema</p>
      </div>
    </div>

    <!-- Tabs -->
    <div class="bg-white shadow rounded-lg">
      <div class="border-b border-gray-200">
        <nav class="-mb-px flex space-x-8 px-6">
          <button
            @click="activeTab = 'dashboard'"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm',
              activeTab === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Dashboard
          </button>
          <button
            @click="activeTab = 'users'"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm',
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Usuarios
          </button>
          <button
            @click="activeTab = 'settings'"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm',
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Configuraciones
          </button>
          <button
            @click="activeTab = 'ai-config'"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm',
              activeTab === 'ai-config'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            IA Configuración
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="p-6">
        <!-- Dashboard Tab -->
        <div v-if="activeTab === 'dashboard'" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div class="bg-blue-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-blue-600">{{ dashboardData.summary?.total_users || 0 }}</div>
              <div class="text-sm text-gray-600">Total Usuarios</div>
            </div>
            <div class="bg-green-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-green-600">{{ dashboardData.summary?.active_users || 0 }}</div>
              <div class="text-sm text-gray-600">Usuarios Activos</div>
            </div>
            <div class="bg-purple-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-purple-600">{{ dashboardData.summary?.total_reports || 0 }}</div>
              <div class="text-sm text-gray-600">Total Reportes</div>
            </div>
            <div class="bg-orange-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-orange-600">${{ totalRevenue }}</div>
              <div class="text-sm text-gray-600">Ingresos Totales</div>
            </div>
          </div>

          <!-- Top Users -->
          <div v-if="dashboardData.top_users?.length" class="bg-white border rounded-lg p-4">
            <h3 class="text-lg font-semibold mb-4">Top Usuarios por Consumo</h3>
            <div class="overflow-x-auto">
              <table class="min-w-full">
                <thead>
                  <tr class="border-b">
                    <th class="text-left py-2">Usuario</th>
                    <th class="text-left py-2">Email</th>
                    <th class="text-right py-2">Reportes</th>
                    <th class="text-right py-2">Costo Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in dashboardData.top_users" :key="user.user_id" class="border-b">
                    <td class="py-2">{{ user.user?.name || 'N/A' }}</td>
                    <td class="py-2">{{ user.user?.email || 'N/A' }}</td>
                    <td class="py-2 text-right">{{ user.report_count }}</td>
                    <td class="py-2 text-right">${{ user.total_cost }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Users Tab -->
        <div v-if="activeTab === 'users'" class="space-y-6">
          <!-- Add User Button -->
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold">Gestión de Usuarios</h3>
            <button
              @click="showAddUserModal = true"
              class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Agregar Usuario
            </button>
          </div>

          <!-- Users Table -->
          <div class="bg-white border rounded-lg overflow-hidden">
            <table class="min-w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cuota</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-for="user in users" :key="user.id">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">{{ user.name }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">{{ user.email }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                      user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    ]">
                      {{ user.role === 'admin' ? 'Admin' : 'Usuario' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">
                      {{ user.quota_used }}/{{ user.monthly_quota }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                      user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    ]">
                      {{ user.status === 'active' ? 'Activo' : 'Inactivo' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      @click="editUser(user)"
                      class="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Editar
                    </button>
                    <button
                      @click="resetUserQuota(user)"
                      class="text-green-600 hover:text-green-900 mr-3"
                    >
                      Reset Cuota
                    </button>
                    <button
                      v-if="user.role !== 'admin'"
                      @click="deleteUser(user)"
                      class="text-red-600 hover:text-red-900"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Settings Tab -->
        <div v-if="activeTab === 'settings'" class="space-y-6">
          <h3 class="text-lg font-semibold">Configuraciones del Sistema</h3>
          
          <div class="bg-white border rounded-lg p-6">
            <form @submit.prevent="updateSettings">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Cuota Mensual por Defecto
                  </label>
                  <input
                    v-model="settingsForm.default_monthly_quota"
                    type="number"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Precio por Reporte (USD)
                  </label>
                  <input
                    v-model="settingsForm.default_price_per_report"
                    type="number"
                    step="0.01"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Moneda
                  </label>
                  <select
                    v-model="settingsForm.currency"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="PEN">PEN</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    OAuth Google Habilitado
                  </label>
                  <input
                    v-model="settingsForm.oauth_google_enabled"
                    type="checkbox"
                    class="rounded"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    OAuth GitHub Habilitado
                  </label>
                  <input
                    v-model="settingsForm.oauth_github_enabled"
                    type="checkbox"
                    class="rounded"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Sistema en Mantenimiento
                  </label>
                  <input
                    v-model="settingsForm.system_maintenance"
                    type="checkbox"
                    class="rounded"
                  />
                </div>
              </div>
              
              <div class="mt-6">
                <button
                  type="submit"
                  class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Guardar Configuraciones
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- AI Config Tab -->
        <div v-if="activeTab === 'ai-config'" class="space-y-6">
          <!-- AI Configuration Section -->
          <div class="bg-white border rounded-lg p-6">
            <h3 class="text-lg font-semibold mb-4">Configuración de IA</h3>
            
            <form @submit.prevent="updateAIConfig">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Proveedor de LLM
                  </label>
                  <select
                    v-model="aiConfigForm.LLM_PROVIDER.value"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="groq">Groq</option>
                    <option value="deepseek">DeepSeek</option>
                    <option value="sambanova">Sambanova</option>
                  </select>
                  <p class="text-xs text-gray-500 mt-1">Selecciona el proveedor de LLM por defecto</p>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    GROQ API Key
                  </label>
                  <div class="relative">
                    <input
                      v-model="aiConfigForm.GROQ_API_KEY.value"
                      :type="showGroqKey ? 'text' : 'password'"
                      class="w-full border border-gray-300 rounded-md px-3 py-2 pr-10"
                      placeholder="sk-..."
                    />
                    <button
                      type="button"
                      @click="showGroqKey = !showGroqKey"
                      class="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                    >
                      {{ showGroqKey ? '👁️' : '👁️‍🗨️' }}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    DEEPSEEK API Key
                  </label>
                  <div class="relative">
                    <input
                      v-model="aiConfigForm.DEEPSEEK_API_KEY.value"
                      :type="showDeepSeekKey ? 'text' : 'password'"
                      class="w-full border border-gray-300 rounded-md px-3 py-2 pr-10"
                      placeholder="sk-..."
                    />
                    <button
                      type="button"
                      @click="showDeepSeekKey = !showDeepSeekKey"
                      class="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                    >
                      {{ showDeepSeekKey ? '👁️' : '👁️‍🗨️' }}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    SAMBANOVA API Key
                  </label>
                  <div class="relative">
                    <input
                      v-model="aiConfigForm.SAMBANOVA_API_KEY.value"
                      :type="showSambanovaKey ? 'text' : 'password'"
                      class="w-full border border-gray-300 rounded-md px-3 py-2 pr-10"
                      placeholder="sk-..."
                    />
                    <button
                      type="button"
                      @click="showSambanovaKey = !showSambanovaKey"
                      class="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                    >
                      {{ showSambanovaKey ? '👁️' : '👁️‍🗨️' }}
                    </button>
                  </div>
                </div>
              </div>
              
              <div class="mt-6">
                <button
                  type="submit"
                  class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  :disabled="savingAIConfig"
                >
                  {{ savingAIConfig ? 'Guardando...' : 'Guardar Configuración de IA' }}
                </button>
              </div>
            </form>
          </div>

          <!-- Prompts Management Section -->
          <div class="bg-white border rounded-lg p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold">Gestión de Prompts</h3>
              <button
                @click="showPromptModal = true; editingPrompt = null"
                class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                Nuevo Prompt
              </button>
            </div>

            <!-- Filters -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Agent</label>
                <select
                  v-model="promptFilters.agent"
                  @change="loadPrompts()"
                  class="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Todos</option>
                  <option value="A">A - Extractor</option>
                  <option value="B">B - Validator</option>
                  <option value="C">C - Clinical</option>
                  <option value="D">D - Auditor</option>
                  <option value="E">E - Corrector</option>
                  <option value="F">F - Patient</option>
                  <option value="G">G - Quality Control</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  v-model="promptFilters.isActive"
                  @change="loadPrompts()"
                  class="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option :value="undefined">Todos</option>
                  <option :value="true">Activos</option>
                  <option :value="false">Inactivos</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                <input
                  v-model="promptFilters.search"
                  @input="loadPrompts()"
                  type="text"
                  placeholder="Buscar por version..."
                  class="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>

            <!-- Prompts Table -->
            <div class="overflow-x-auto">
              <table class="min-w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Versión</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actualizado</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr v-for="prompt in prompts" :key="prompt.id">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="text-sm font-medium text-gray-900">{{ prompt.agent }}</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="text-sm text-gray-900">{{ prompt.version }}</span>
                    </td>
                    <td class="px-6 py-4">
                      <span class="text-sm text-gray-500">{{ prompt.description || '-' }}</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="[
                        'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                        prompt.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      ]">
                        {{ prompt.is_active ? 'Activo' : 'Inactivo' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="text-sm text-gray-500">{{ formatDate(prompt.updatedAt) }}</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        @click="editPrompt(prompt)"
                        class="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Editar
                      </button>
                      <button
                        @click="deletePrompt(prompt)"
                        class="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="promptPagination.pages > 1" class="mt-4 flex justify-between items-center">
              <div class="text-sm text-gray-700">
                Página {{ promptPagination.page }} de {{ promptPagination.pages }}
              </div>
              <div class="flex gap-2">
                <button
                  @click="changePromptPage(promptPagination.page - 1)"
                  :disabled="promptPagination.page <= 1"
                  class="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  @click="changePromptPage(promptPagination.page + 1)"
                  :disabled="promptPagination.page >= promptPagination.pages"
                  class="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Prompt Editor Modal -->
    <div v-if="showPromptModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h3 class="text-lg font-semibold mb-4">
          {{ editingPrompt ? 'Editar Prompt' : 'Nuevo Prompt' }}
        </h3>
        <form @submit.prevent="savePrompt">
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Agent</label>
                <select
                  v-model="promptForm.agent"
                  required
                  class="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="A">A - Extractor</option>
                  <option value="B">B - Validator</option>
                  <option value="C">C - Clinical</option>
                  <option value="D">D - Auditor</option>
                  <option value="E">E - Corrector</option>
                  <option value="F">F - Patient</option>
                  <option value="G">G - Quality Control</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Versión</label>
                <input
                  v-model="promptForm.version"
                  type="text"
                  required
                  pattern="^v\d+\.\d+$"
                  placeholder="v1.0"
                  class="w-full border border-gray-300 rounded-md px-3 py-2"
                />
                <p class="text-xs text-gray-500 mt-1">Formato: v1.0, v2.0, etc.</p>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <input
                v-model="promptForm.description"
                type="text"
                class="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Contenido</label>
              <textarea
                v-model="promptForm.content"
                required
                rows="20"
                class="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                placeholder="Escribe el contenido del prompt aquí..."
              ></textarea>
              <p class="text-xs text-gray-500 mt-1">
                {{ promptForm.content.length }} caracteres (mínimo 10, máximo 1,000,000)
              </p>
            </div>
            <div>
              <label class="flex items-center">
                <input
                  v-model="promptForm.is_active"
                  type="checkbox"
                  class="rounded mr-2"
                />
                <span class="text-sm text-gray-700">Activo</span>
              </label>
            </div>
          </div>
          <div class="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              @click="showPromptModal = false"
              class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              :disabled="savingPrompt"
            >
              {{ savingPrompt ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add User Modal -->
    <div v-if="showAddUserModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">Agregar Usuario</h3>
        <form @submit.prevent="createUser">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input
                v-model="newUser.name"
                type="text"
                required
                class="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                v-model="newUser.email"
                type="email"
                required
                class="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
              <input
                v-model="newUser.password"
                type="password"
                class="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Rol</label>
              <select
                v-model="newUser.role"
                class="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="user">Usuario</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Cuota Mensual</label>
              <input
                v-model="newUser.monthly_quota"
                type="number"
                class="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>
          <div class="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              @click="showAddUserModal = false"
              class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Crear Usuario
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div v-if="showEditUserModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 class="text-lg font-semibold mb-4">Editar Usuario</h3>
        <form @submit.prevent="saveUser">
          <div class="space-y-4">
            <!-- Información básica -->
            <div class="border-b pb-4">
              <h4 class="font-medium mb-3">Información Básica</h4>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    v-model="editUserForm.name"
                    type="text"
                    required
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    v-model="editUserForm.email"
                    type="email"
                    required
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                  <select
                    v-model="editUserForm.role"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="user">Usuario</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    v-model="editUserForm.status"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                    <option value="suspended">Suspendido</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Cuota Mensual</label>
                  <input
                    v-model="editUserForm.monthly_quota"
                    type="number"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nueva Contraseña (opcional)</label>
                  <input
                    v-model="editUserForm.password"
                    type="password"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
            </div>

            <!-- Configuración de Cabecera de Reportes -->
            <div class="border-b pb-4">
              <h4 class="font-medium mb-3">Cabecera Personalizada para Reportes PDF</h4>
              <p class="text-xs text-gray-500 mb-3">Configure estos datos para personalizar la cabecera de los reportes PDF generados por este usuario.</p>
              
              <!-- Upload Logo -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                <div class="flex items-center gap-4">
                  <div v-if="editUserForm.header_logo_path" class="w-20 h-20 border rounded flex items-center justify-center">
                    <img 
                      :src="resolveLogoUrl(editUserForm.header_logo_path)" 
                      alt="Logo"
                      class="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div v-else class="w-20 h-20 border rounded flex items-center justify-center text-gray-400 text-xs">
                    Sin logo
                  </div>
                  <div class="flex-1">
                    <input
                      type="file"
                      ref="logoFileInput"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      @change="handleLogoUpload"
                      class="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p class="text-xs text-gray-500 mt-1">Tamaño máximo: 2MB. Formatos: JPG, PNG, GIF, WEBP</p>
                  </div>
                </div>
              </div>

              <!-- Campos de cabecera -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                  <input
                    v-model="editUserForm.header_address"
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Av. Principal 123"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Correo</label>
                  <input
                    v-model="editUserForm.header_email"
                    type="email"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="contacto@ejemplo.com"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    v-model="editUserForm.header_phone"
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="+51 999 999 999"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">RUC</label>
                  <input
                    v-model="editUserForm.header_ruc"
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="20123456789"
                  />
                </div>
              </div>
            </div>
            
            <!-- Firma Autorizada -->
            <div class="border-b pb-4 mt-4">
              <h4 class="font-medium mb-3">Firma Autorizada</h4>
              <p class="text-xs text-gray-500 mb-3">Estos datos aparecerán en la esquina inferior derecha del reporte PDF.</p>
              
              <!-- Upload Signature -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Imagen de la Firma</label>
                <div class="flex items-center gap-4">
                  <div v-if="editUserForm.extra_data.signature?.path" class="w-32 h-16 border rounded flex items-center justify-center bg-gray-50">
                    <img 
                      :src="resolveLogoUrl(editUserForm.extra_data.signature.path)" 
                      alt="Firma"
                      class="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div v-else class="w-32 h-16 border rounded flex items-center justify-center text-gray-400 text-xs text-center p-2">
                    Sin firma
                  </div>
                  <div class="flex-1">
                    <input
                      type="file"
                      ref="signatureFileInput"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      @change="handleSignatureUpload"
                      class="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p class="text-xs text-gray-500 mt-1">Tamaño máximo: 2MB. Formatos: JPG, PNG, GIF, WEBP</p>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    v-model="editUserForm.extra_data.signature.name"
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Dr. Juan Perez"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
                  <input
                    v-model="editUserForm.extra_data.signature.role"
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Director Médico"
                  />
                </div>
                <div class="md:col-span-1">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Credenciales</label>
                  <input
                    v-model="editUserForm.extra_data.signature.credentials"
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="CMP: 12345 / RNE: 6789"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              @click="showEditUserModal = false"
              class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              :disabled="savingUser"
            >
              {{ savingUser ? 'Guardando...' : 'Guardar Cambios' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useAuthStore } from '../stores/auth';
import { AIConfigService } from '../services/AIConfigService';
import type { PromptListItem, Prompt } from '../services/AIConfigService';

const authStore = useAuthStore();

// State
const activeTab = ref('dashboard');
const showAddUserModal = ref(false);
const users = ref<any[]>([]);
const dashboardData = ref<any>({});
const settings = ref<any>({});
const revenueData = ref<{ total_revenue: number }>({ total_revenue: 0 });

// AI Config State
const aiConfigForm = ref({
  GROQ_API_KEY: { value: '', type: 'string' },
  DEEPSEEK_API_KEY: { value: '', type: 'string' },
  SAMBANOVA_API_KEY: { value: '', type: 'string' },
  LLM_PROVIDER: { value: 'groq', type: 'string' },
});
const showGroqKey = ref(false);
const showDeepSeekKey = ref(false);
const showSambanovaKey = ref(false);
const savingAIConfig = ref(false);

// Prompts State
const prompts = ref<PromptListItem[]>([]);
const promptPagination = ref({ page: 1, limit: 20, total: 0, pages: 1 });
const promptFilters = ref({ agent: '', isActive: undefined as boolean | undefined, search: '' });
const showPromptModal = ref(false);
const editingPrompt = ref<PromptListItem | null>(null);
const promptForm = ref({
  agent: 'A',
  version: 'v1.0',
  content: '',
  description: '',
  is_active: true,
});
const savingPrompt = ref(false);

// Forms
const newUser = ref({
  name: '',
  email: '',
  password: '',
  role: 'user',
  monthly_quota: 10
});

const settingsForm = ref({
  default_monthly_quota: 10,
  default_price_per_report: 2.5,
  currency: 'USD',
  oauth_google_enabled: true,
  oauth_github_enabled: true,
  system_maintenance: false
});

// Computed
const totalRevenue = computed(() => {
  return revenueData.value.total_revenue || 0;
});

// Methods
const loadRevenue = async () => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/revenue`, {
      headers: {
        'Authorization': `Bearer ${authStore.accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      revenueData.value = await response.json();
    }
  } catch (error) {
    console.error('Error cargando ingresos:', error);
  }
};

const loadUsers = async () => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/users`, {
      headers: {
        'Authorization': `Bearer ${authStore.accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      }
    });
    const data = await response.json();
    users.value = data.users;
  } catch (error) {
    console.error('Error loading users:', error);
  }
};

const loadDashboard = async () => {
  try {
    console.log('🔄 Loading dashboard data...');
    console.log('🔑 Auth token:', authStore.accessToken ? 'Present' : 'Missing');
    
    // Agregar timestamp para evitar caché
    const timestamp = Date.now();
    const url = `${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/dashboard?t=${timestamp}`;
    console.log('🌐 Dashboard URL:', url);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${authStore.accessToken}`,
        'ngrok-skip-browser-warning': 'true',
        'Cache-Control': 'no-cache'
      }
    });
    
    console.log('📡 Dashboard response status:', response.status);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('📊 Dashboard data received:', data);
    dashboardData.value = data;
  } catch (error) {
    console.error('❌ Error loading dashboard:', error);
  }
};

const loadSettings = async () => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/settings`, {
      headers: {
        'Authorization': `Bearer ${authStore.accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      }
    });
    const data = await response.json();
    settings.value = data;
    
    // Populate form
    settingsForm.value = {
      default_monthly_quota: data.default_monthly_quota?.value || 10,
      default_price_per_report: data.default_price_per_report?.value || 2.5,
      currency: data.currency?.value || 'USD',
      oauth_google_enabled: data.oauth_google_enabled?.value || false,
      oauth_github_enabled: data.oauth_github_enabled?.value || false,
      system_maintenance: data.system_maintenance?.value || false
    };
  } catch (error) {
    console.error('Error loading settings:', error);
  }
};

const createUser = async () => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authStore.accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      },
      body: JSON.stringify(newUser.value)
    });
    
    if (response.ok) {
      showAddUserModal.value = false;
      newUser.value = { name: '', email: '', password: '', role: 'user', monthly_quota: 10 };
      loadUsers();
    }
  } catch (error) {
    console.error('Error creating user:', error);
  }
};

// Edit User State
const showEditUserModal = ref(false);
const editingUser = ref<any>(null);
const savingUser = ref(false);
const editUserForm = ref({
  name: '',
  email: '',
  role: 'user',
  status: 'active',
  monthly_quota: 10,
  password: '',
  header_address: '',
  header_email: '',
  header_phone: '',
  header_ruc: '',
  header_logo_path: null as string | null,
  extra_data: {
    signature: {
      name: '',
      role: '',
      credentials: '',
      path: ''
    }
  }
});
const logoFileInput = ref<HTMLInputElement | null>(null);
const signatureFileInput = ref<HTMLInputElement | null>(null);

const editUser = (user: any) => {
  editingUser.value = user;
  editUserForm.value = {
    name: user.name || '',
    email: user.email || '',
    role: user.role || 'user',
    status: user.status || 'active',
    monthly_quota: user.monthly_quota || 10,
    password: '',
    header_address: user.header_address || '',
    header_email: user.header_email || '',
    header_phone: user.header_phone || '',
    header_ruc: user.header_ruc || '',
    header_logo_path: user.header_logo_path || null,
    extra_data: user.extra_data || {
      signature: {
        name: '',
        role: '',
        credentials: '',
        path: ''
      }
    }
  };
  // Asegurar estructura de firma
  if (!editUserForm.value.extra_data.signature) {
    editUserForm.value.extra_data.signature = { name: '', role: '', credentials: '', path: '' };
  }
  showEditUserModal.value = true;
};

const handleLogoUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  if (file.size > 2 * 1024 * 1024) {
    alert('El archivo es demasiado grande. Tamaño máximo: 2MB');
    return;
  }

  const formData = new FormData();
  formData.append('logo', file);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/users/${editingUser.value.id}/logo`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authStore.accessToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: formData,
      }
    );

    if (response.ok) {
      const data = await response.json();
      editUserForm.value.header_logo_path = data.logo_path;
      alert('Logo subido exitosamente');
    } else {
      const error = await response.json();
      alert('Error subiendo logo: ' + (error.error || 'Error desconocido'));
    }
  } catch (error: any) {
    console.error('Error subiendo logo:', error);
    alert('Error subiendo logo: ' + (error.message || 'Error desconocido'));
  }
};

const handleSignatureUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  if (file.size > 2 * 1024 * 1024) {
    alert('El archivo es demasiado grande. Tamaño máximo: 2MB');
    return;
  }

  const formData = new FormData();
  formData.append('signature', file);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/users/${editingUser.value.id}/signature`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authStore.accessToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: formData,
      }
    );

    if (response.ok) {
      const data = await response.json();
      editUserForm.value.extra_data.signature.path = data.signature_path;
      alert('Firma subida exitosamente');
    } else {
      const error = await response.json();
      alert('Error subiendo firma: ' + (error.error || 'Error desconocido'));
    }
  } catch (error: any) {
    console.error('Error subiendo firma:', error);
    alert('Error subiendo firma: ' + (error.message || 'Error desconocido'));
  }
};

const saveUser = async () => {
  savingUser.value = true;
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/users/${editingUser.value.id}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authStore.accessToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          name: editUserForm.value.name,
          email: editUserForm.value.email,
          role: editUserForm.value.role,
          status: editUserForm.value.status,
          monthly_quota: editUserForm.value.monthly_quota,
          password: editUserForm.value.password || undefined,
          header_address: editUserForm.value.header_address || null,
          header_email: editUserForm.value.header_email || null,
          header_phone: editUserForm.value.header_phone || null,
          header_ruc: editUserForm.value.header_ruc || null,
          extra_data: editUserForm.value.extra_data || null,
        }),
      }
    );

    if (response.ok) {
      showEditUserModal.value = false;
      editingUser.value = null;
      editUserForm.value = {
        name: '',
        email: '',
        role: 'user',
        status: 'active',
        monthly_quota: 10,
        password: '',
        header_address: '',
        header_email: '',
        header_phone: '',
        header_ruc: '',
        header_logo_path: null,
        extra_data: {
          signature: {
            name: '',
            role: '',
            credentials: '',
            path: ''
          }
        }
      };
      loadUsers();
      alert('Usuario actualizado exitosamente');
    } else {
      const error = await response.json();
      alert('Error actualizando usuario: ' + (error.error || 'Error desconocido'));
    }
  } catch (error: any) {
    console.error('Error actualizando usuario:', error);
    alert('Error actualizando usuario: ' + (error.message || 'Error desconocido'));
  } finally {
    savingUser.value = false;
  }
};

const resetUserQuota = async (user: any) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/users/${user.id}/quota`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authStore.accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      },
      body: JSON.stringify({ reset_used: true })
    });
    
    if (response.ok) {
      loadUsers();
    }
  } catch (error) {
    console.error('Error resetting quota:', error);
  }
};

const deleteUser = async (user: any) => {
  if (!confirm(`¿Estás seguro de eliminar al usuario ${user.name}?`)) return;
  
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/users/${user.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authStore.accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      }
    });
    
    if (response.ok) {
      loadUsers();
    }
  } catch (error) {
    console.error('Error deleting user:', error);
  }
};

const updateSettings = async () => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8010'}/api/admin/settings`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authStore.accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      },
      body: JSON.stringify(settingsForm.value)
    });
    
    if (response.ok) {
      alert('Configuraciones actualizadas exitosamente');
    }
  } catch (error) {
    console.error('Error updating settings:', error);
  }
};

// AI Config Methods
const loadAIConfig = async () => {
  try {
    const config = await AIConfigService.getAIConfig(authStore.accessToken);
    aiConfigForm.value = {
      GROQ_API_KEY: config.GROQ_API_KEY || { value: '', type: 'string' },
      DEEPSEEK_API_KEY: config.DEEPSEEK_API_KEY || { value: '', type: 'string' },
      SAMBANOVA_API_KEY: config.SAMBANOVA_API_KEY || { value: '', type: 'string' },
      LLM_PROVIDER: config.LLM_PROVIDER || { value: 'groq', type: 'string' },
    };
  } catch (error) {
    console.error('Error cargando configuración de IA:', error);
  }
};

const updateAIConfig = async () => {
  savingAIConfig.value = true;
  try {
    await AIConfigService.updateAIConfig(authStore.accessToken, aiConfigForm.value);
    alert('Configuración de IA actualizada exitosamente');
  } catch (error: any) {
    alert('Error actualizando configuración: ' + (error.message || 'Error desconocido'));
  } finally {
    savingAIConfig.value = false;
  }
};

// Prompts Methods
const loadPrompts = async () => {
  try {
    const response = await AIConfigService.getPrompts(
      authStore.accessToken,
      promptPagination.value.page,
      promptPagination.value.limit,
      promptFilters.value.agent || undefined,
      promptFilters.value.isActive
    );
    prompts.value = response.prompts;
    promptPagination.value = response.pagination;
    
    // Filtrar por búsqueda en cliente (mejor hacer en servidor)
    if (promptFilters.value.search) {
      prompts.value = prompts.value.filter(p =>
        p.version.toLowerCase().includes(promptFilters.value.search.toLowerCase())
      );
    }
  } catch (error) {
    console.error('Error cargando prompts:', error);
  }
};

const changePromptPage = (page: number) => {
  promptPagination.value.page = page;
  loadPrompts();
};

const editPrompt = async (prompt: PromptListItem) => {
  try {
    const fullPrompt = await AIConfigService.getPrompt(authStore.accessToken, prompt.id);
    promptForm.value = {
      agent: fullPrompt.agent,
      version: fullPrompt.version,
      content: fullPrompt.content,
      description: fullPrompt.description || '',
      is_active: fullPrompt.is_active,
    };
    editingPrompt.value = prompt;
    showPromptModal.value = true;
  } catch (error) {
    console.error('Error cargando prompt:', error);
    alert('Error cargando prompt para editar');
  }
};

const deletePrompt = async (prompt: PromptListItem) => {
  if (!confirm(`¿Estás seguro de eliminar el prompt ${prompt.agent} ${prompt.version}?`)) return;
  
  try {
    await AIConfigService.deletePrompt(authStore.accessToken, prompt.id);
    loadPrompts();
  } catch (error) {
    console.error('Error eliminando prompt:', error);
    alert('Error eliminando prompt');
  }
};

const savePrompt = async () => {
  savingPrompt.value = true;
  try {
    if (editingPrompt.value) {
      // Actualizar
      await AIConfigService.updatePrompt(authStore.accessToken, editingPrompt.value.id, promptForm.value);
    } else {
      // Crear
      await AIConfigService.createPrompt(authStore.accessToken, promptForm.value);
    }
    showPromptModal.value = false;
    editingPrompt.value = null;
    promptForm.value = {
      agent: 'A',
      version: 'v1.0',
      content: '',
      description: '',
      is_active: true,
    };
    loadPrompts();
  } catch (error: any) {
    alert('Error guardando prompt: ' + (error.message || 'Error desconocido'));
  } finally {
    savingPrompt.value = false;
  }
};

// URL base de API para construir URLs absolutas
const API_BASE: string = (import.meta as any).env.VITE_API_URL || (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:8010';

// Resolver URL del logo (acepta URLs absolutas - p.ej. Cloudinary - o paths relativos del backend)
const resolveLogoUrl = (path: string | null): string => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API_BASE}/${path}`;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('es-ES');
};

// Watch activeTab para cargar datos cuando se cambia
watch(activeTab, (newTab) => {
  if (newTab === 'ai-config') {
    loadAIConfig();
    loadPrompts();
  }
});

// Lifecycle
onMounted(() => {
  console.log('🔄 Admin component mounted, loading data...');
  loadUsers();
  loadDashboard();
  loadSettings();
  loadRevenue();
  // No cargar IA config hasta que se active el tab para evitar carga innecesaria
});
</script>
